// Login Page with Social Authentication

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Shield,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Button from '../components/ui/Button';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Simulate API call - replace with actual authentication
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Demo admin accounts
      const adminAccounts = {
        'superadmin@civicvoiceofindia.org': { 
          password: 'admin123', 
          role: 'super_admin',
          name: 'Super Admin',
          permissions: ['manage_admins', 'manage_chapters', 'manage_roles', 'view_analytics']
        },
        'mumbai@civicvoiceofindia.org': { 
          password: 'mumbai123', 
          role: 'chapter_admin',
          name: 'Mumbai Admin',
          chapter: 'Mumbai Central Chapter',
          chapter_id: 1,
          permissions: ['manage_users', 'manage_events', 'view_reports']
        },
        'delhi@civicvoiceofindia.org': { 
          password: 'delhi123', 
          role: 'chapter_admin',
          name: 'Delhi Admin',
          chapter: 'Delhi NCR Chapter',
          chapter_id: 2,
          permissions: ['manage_users', 'manage_events', 'view_reports']
        }
      };
      
      if (formData.email && formData.password) {
        const admin = adminAccounts[formData.email as keyof typeof adminAccounts];
        
        if (admin && admin.password === formData.password) {
          // Successful admin login
          const userData = {
            email: formData.email,
            name: admin.name,
            role: admin.role,
            chapter: admin.chapter || null,
            chapter_id: admin.chapter_id || null,
            permissions: admin.permissions,
            avatar: `https://ui-avatars.com/api/?name=${admin.name.replace(' ', '+')}&background=3b82f6&color=fff`
          };
          
          localStorage.setItem('auth_token', `admin-token-${admin.role}-${Date.now()}`);
          localStorage.setItem('user_data', JSON.stringify(userData));
          
          // Redirect based on role
          if (admin.role === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/admin/chapter-dashboard');
          }
        } else {
          setError('Invalid credentials. Please check your username and password.');
        }
      } else {
        setError('Please enter both username and password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full space-y-8"
      >
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/civic-voice-logo.png" 
              alt="Civic Voice of India" 
              className="w-24 h-24"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Admin Login
          </h2>
          <p className="text-gray-600">
            Access your administrative dashboard
          </p>
        </div>


        {/* Email Login Form */}
        <form onSubmit={handleEmailLogin} className="space-y-6">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2"
            >
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}

          <div className="space-y-4">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Username / Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Enter your username or email"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center space-x-2 py-3"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </form>

        {/* Admin Access Notice */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Administrative access only.{' '}
            <span className="text-gray-500">
              Contact super admin for account creation.
            </span>
          </p>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">Secure Login</h4>
              <p className="text-xs text-blue-700">
                Your data is protected with end-to-end encryption and secure authentication protocols.
              </p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-200">
          <div className="text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-900">Secure Access</h4>
            <p className="text-xs text-gray-600">Protected login</p>
          </div>
          <div className="text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-900">Quick Login</h4>
            <p className="text-xs text-gray-600">Google integration</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;